import { useEffect, useState, useMemo } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';
import {
  Card, SectionHeader, Table, EmptyState, Modal, Textarea,
  Btn,
} from './ui';

type Lead   = Schema['Lead']['type'];
type Client = Schema['Client']['type'];
type SortField = 'name' | 'email' | 'status' | 'createdAt' | 'company' | 'source';

const STATUS_COLORS: Record<string, string> = {
  new:       '#3b82f6',
  contacted: '#f59e0b',
  converted: '#22c55e',
  lost:      '#64748b',
};

const SELECT_STYLE: React.CSSProperties = {
  background: '#0d1420', color: '#e8edf5', border: '1px solid #1e2d42',
  borderRadius: 7, padding: '8px 12px', fontSize: 13,
};

const BLANK_FORM = {
  name: '', email: '', company: '', phone: '', source: 'manual',
  clientId: '', status: 'new' as Lead['status'], notes: '',
};

export default function Leads({ initialSource }: { initialSource?: string } = {}) {
  const db = useMemo(() => generateClient<Schema>(), []);
  const [leads,         setLeads]         = useState<Lead[]>([]);
  const [clients,       setClients]       = useState<Client[]>([]);
  const [loading,       setLoading]       = useState(true);

  // Filters & sort
  const [search,        setSearch]        = useState('');
  const [clientFilter,  setClientFilter]  = useState('');
  const [statusFilter,  setStatusFilter]  = useState('');
  const [sourceFilter,  setSourceFilter]  = useState(initialSource ?? '');
  const [showArchived,  setShowArchived]  = useState(false);
  const [sortField,     setSortField]     = useState<SortField>('createdAt');
  const [sortDir,       setSortDir]       = useState<'asc' | 'desc'>('desc');

  // Selection & bulk
  const [selected,      setSelected]      = useState<Set<string>>(new Set());
  const [bulkStatus,    setBulkStatus]    = useState('');
  const [bulkWorking,   setBulkWorking]   = useState(false);

  // Modals
  const [noteModal,     setNoteModal]     = useState<Lead | null>(null);
  const [noteText,      setNoteText]      = useState('');
  const [saving,        setSaving]        = useState(false);
  const [addModal,      setAddModal]      = useState(false);
  const [addForm,       setAddForm]       = useState(BLANK_FORM);
  const [addSaving,     setAddSaving]     = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const sub = db.models.Lead.observeQuery().subscribe(({ items, isSynced }) => {
      setLeads(items.filter(Boolean) as Lead[]);
      if (isSynced) setLoading(false);
    });
    db.models.Client.list().then(({ data }) =>
      setClients((data ?? []).filter(Boolean) as Client[])
    );
    return () => sub.unsubscribe();
  }, []);

  // â”€â”€ Derived filtered + sorted list â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const filtered = useMemo(() => {
    let list = leads.filter(l => {
      if (!showArchived && l.archived) return false;
      if (showArchived && !l.archived) return false;
      if (clientFilter && l.clientId !== clientFilter) return false;
      if (statusFilter && l.status   !== statusFilter)  return false;
      if (sourceFilter && l.source   !== sourceFilter)  return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !l.name?.toLowerCase().includes(q) &&
          !l.email?.toLowerCase().includes(q) &&
          !l.company?.toLowerCase().includes(q) &&
          !l.phone?.includes(q)
        ) return false;
      }
      return true;
    });

    list = [...list].sort((a, b) => {
      let av = '', bv = '';
      if (sortField === 'createdAt') {
        const at = new Date(a.createdAt ?? 0).getTime();
        const bt = new Date(b.createdAt ?? 0).getTime();
        return sortDir === 'asc' ? at - bt : bt - at;
      }
      av = (a[sortField] ?? '') as string;
      bv = (b[sortField] ?? '') as string;
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });

    return list;
  }, [leads, showArchived, clientFilter, statusFilter, sourceFilter, search, sortField, sortDir]);

  const allSelected  = filtered.length > 0 && filtered.every(l => selected.has(l.id));
  const someSelected = filtered.some(l => selected.has(l.id));
  const selectedList = filtered.filter(l => selected.has(l.id));

  // â”€â”€ Sort helper â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  function toggleSort(field: SortField) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }
  const sortIcon = (field: SortField) =>
    sortField !== field ? ' \u2195' : sortDir === 'asc' ? ' \u2191' : ' \u2193';

  // â”€â”€ Selection helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  function toggleOne(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(filtered.map(l => l.id)));
  }

  // â”€â”€ Bulk actions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  async function bulkUpdateStatus() {
    if (!bulkStatus || selectedList.length === 0) return;
    setBulkWorking(true);
    try {
      await Promise.all(selectedList.map(l =>
        db.models.Lead.update({ id: l.id, status: bulkStatus as Lead['status'] })
      ));
      setSelected(new Set());
      setBulkStatus('');
      showToast(`Updated ${selectedList.length} lead(s) to "${bulkStatus}"`);
    } catch (e: any) {
      showToast('Bulk update failed: ' + e.message, 'error');
    } finally {
      setBulkWorking(false);
    }
  }

  async function bulkArchive(archive: boolean) {
    if (selectedList.length === 0) return;
    setBulkWorking(true);
    try {
      await Promise.all(selectedList.map(l =>
        db.models.Lead.update({ id: l.id, archived: archive })
      ));
      setSelected(new Set());
      showToast(`${archive ? 'Archived' : 'Unarchived'} ${selectedList.length} lead(s)`);
    } catch (e: any) {
      showToast('Archive failed: ' + e.message, 'error');
    } finally {
      setBulkWorking(false);
    }
  }

  async function bulkDelete() {
    if (selectedList.length === 0) return;
    setBulkWorking(true);
    try {
      await Promise.all(selectedList.map(l => db.models.Lead.delete({ id: l.id })));
      setSelected(new Set());
      setConfirmDelete(false);
      showToast(`Deleted ${selectedList.length} lead(s)`);
    } catch (e: any) {
      showToast('Delete failed: ' + e.message, 'error');
    } finally {
      setBulkWorking(false);
    }
  }

  // â”€â”€ Per-row status update â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  async function updateStatus(lead: Lead, status: Lead['status']) {
    try {
      await db.models.Lead.update({ id: lead.id, status });
    } catch (e: any) {
      showToast('Update failed: ' + e.message, 'error');
    }
  }

  // â”€â”€ Note modal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  async function saveNote() {
    if (!noteModal) return;
    setSaving(true);
    try {
      await db.models.Lead.update({ id: noteModal.id, notes: noteText });
      showToast('Note saved');
      setNoteModal(null);
    } catch (e: any) {
      showToast('Save failed: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  // â”€â”€ Add lead manually â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  async function addLead() {
    if (!addForm.name.trim() || !addForm.email.trim()) {
      showToast('Name and email are required', 'error'); return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(addForm.email)) {
      showToast('Invalid email address', 'error'); return;
    }
    setAddSaving(true);
    try {
      await db.models.Lead.create({
        name:     addForm.name.trim(),
        email:    addForm.email.trim().toLowerCase(),
        company:  addForm.company.trim() || undefined,
        phone:    addForm.phone.trim()   || undefined,
        source:   addForm.source || 'manual',
        clientId: addForm.clientId       || undefined,
        status:   addForm.status,
        notes:    addForm.notes.trim()   || undefined,
      });
      setAddForm(BLANK_FORM);
      setAddModal(false);
      showToast('Lead added');
    } catch (e: any) {
      showToast('Failed to add lead: ' + e.message, 'error');
    } finally {
      setAddSaving(false);
    }
  }

  const clientMap = Object.fromEntries(clients.map(c => [c.id, c.clientName]));

  // ── CSV export ────────────────────────────────────────────────────────────
  function exportCSV(rows: Lead[]) {
    const cols = ['Name', 'Email', 'Phone', 'Company', 'Source', 'Client', 'Status', 'Notes', 'Received'];
    const escape = (v: string | null | undefined) => '"' + (v ?? '').replace(/"/g, '""') + '"';
    const lines = [
      cols.join(','),
      ...rows.map(l => [
        escape(l.name),
        escape(l.email),
        escape(l.phone),
        escape(l.company),
        escape(l.source),
        escape(clientMap[l.clientId ?? ''] ?? ''),
        escape(l.status),
        escape(l.notes),
        escape(l.createdAt ? new Date(l.createdAt).toLocaleString() : ''),
      ].join(','))
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const stats = {
    total:     leads.filter(l => !l.archived).length,
    new:       leads.filter(l => !l.archived && (l.status === 'new' || !l.status)).length,
    contacted: leads.filter(l => !l.archived && l.status === 'contacted').length,
    converted: leads.filter(l => !l.archived && l.status === 'converted').length,
  };

  const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
    'meta-lead-ad':       { label: 'Meta Ad',      color: '#3b82f6' },
    'website-form':       { label: 'Website Form', color: '#10b981' },
    'landing-page':       { label: 'Landing Page', color: '#8b5cf6' },
    'landing-page-hero':  { label: 'Landing Page', color: '#8b5cf6' },
    'manual':             { label: 'Manual',       color: '#64748b' },
    'referral':           { label: 'Referral',     color: '#f59e0b' },
    'other':              { label: 'Other',        color: '#64748b' },
  };

  const sourceLabel = (s: string | null | undefined) => {
    if (!s) return <span style={{ color: '#4a5d7a' }}>-</span>;
    const entry = SOURCE_LABELS[s];
    return (
      <span style={{
        display: 'inline-block', padding: '2px 8px',
        borderRadius: 4, fontSize: 11, fontWeight: 600,
        background: (entry?.color ?? '#64748b') + '22',
        color: entry?.color ?? '#94a3b8',
        letterSpacing: '0.03em',
      }}>
        {entry?.label ?? s}
      </span>
    );
  };

  const thStyle = (field?: SortField): React.CSSProperties => ({
    cursor: field ? 'pointer' : 'default',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    color: sortField === field ? '#3b82f6' : undefined,
  });

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1280 }}>
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          background: toast.type === 'error' ? '#7f1d1d' : '#14532d',
          color: '#fff', padding: '12px 20px', borderRadius: 10,
          boxShadow: '0 4px 24px rgba(0,0,0,0.4)', fontSize: 14,
        }}>{toast.msg}</div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
        <SectionHeader
          title="Leads"
          subtitle="Meta Instant Form and landing page submissions"
        />
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="ghost" onClick={() => exportCSV(filtered)}
            style={{ color: '#34d399', borderColor: '#064e3b' }}>
            Export All
          </Btn>
          <Btn variant="primary" onClick={() => { setAddForm(BLANK_FORM); setAddModal(true); }}>
            + Add Lead
          </Btn>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { label: 'Total',     value: stats.total,     color: '#e8edf5' },
          { label: 'New',       value: stats.new,       color: '#3b82f6' },
          { label: 'Contacted', value: stats.contacted,  color: '#f59e0b' },
          { label: 'Converted', value: stats.converted,  color: '#22c55e' },
        ].map(s => (
          <Card key={s.label} style={{ padding: '14px 20px', minWidth: 110, textAlign: 'center' }}>
            <div style={{ fontSize: 26, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{s.label}</div>
          </Card>
        ))}
      </div>

      {/* Filters row */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search name, email, company..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            ...SELECT_STYLE, minWidth: 220, padding: '8px 12px',
          }}
        />
        <select value={clientFilter} onChange={e => setClientFilter(e.target.value)} style={{ ...SELECT_STYLE, minWidth: 160 }}>
          <option value="">All clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.clientName}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...SELECT_STYLE, minWidth: 140 }}>
          <option value="">All statuses</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="converted">Converted</option>
          <option value="lost">Lost</option>
        </select>
        <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} style={{ ...SELECT_STYLE, minWidth: 150 }}>
          <option value="">All sources</option>
          <option value="meta-lead-ad">Meta Ad</option>
          <option value="website-form">Website Form</option>
          <option value="landing-page">Landing Page</option>
          <option value="manual">Manual</option>
          <option value="referral">Referral</option>
          <option value="other">Other</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#94a3b8', cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={showArchived} onChange={e => { setShowArchived(e.target.checked); setSelected(new Set()); }} />
          Show archived
        </label>
        {(search || clientFilter || statusFilter || sourceFilter) && (
          <Btn size="sm" variant="ghost" onClick={() => { setSearch(''); setClientFilter(''); setStatusFilter(''); setSourceFilter(''); }}>
            Clear filters
          </Btn>
        )}
      </div>

      {/* Bulk action bar */}
      {someSelected && (
        <div style={{
          display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
          background: '#0d1825', border: '1px solid #1e3a5f', borderRadius: 8,
          padding: '10px 16px', marginBottom: 12,
        }}>
          <span style={{ fontSize: 13, color: '#60a5fa', fontWeight: 600 }}>
            {selectedList.length} selected
          </span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <select
              value={bulkStatus}
              onChange={e => setBulkStatus(e.target.value)}
              style={{ ...SELECT_STYLE, padding: '6px 10px', minWidth: 130 }}
            >
              <option value="">Set status...</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="converted">Converted</option>
              <option value="lost">Lost</option>
            </select>
            <Btn size="sm" variant="primary" loading={bulkWorking} onClick={bulkUpdateStatus} disabled={!bulkStatus}>
              Apply
            </Btn>
          </div>
          {!showArchived && (
            <Btn size="sm" variant="ghost" loading={bulkWorking} onClick={() => bulkArchive(true)}>
              Archive
            </Btn>
          )}
          {showArchived && (
            <Btn size="sm" variant="ghost" loading={bulkWorking} onClick={() => bulkArchive(false)}>
              Unarchive
            </Btn>
          )}
          <Btn size="sm" variant="ghost" loading={bulkWorking}
            onClick={() => setConfirmDelete(true)}
            style={{ color: '#f87171', borderColor: '#7f1d1d' }}>
            Delete
          </Btn>
          <Btn size="sm" variant="ghost" onClick={() => exportCSV(selectedList)}
            style={{ color: '#34d399', borderColor: '#064e3b' }}>
            Export CSV
          </Btn>
          <Btn size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Deselect
          </Btn>
        </div>
      )}

      {loading ? (
        <div style={{ color: '#64748b', padding: 32 }}>Loading leads...</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={showArchived ? 'Archive' : 'Inbox'}
          title={showArchived ? 'No archived leads' : 'No leads yet'}
          description={showArchived
            ? 'Archived leads will appear here.'
            : 'Leads will appear here when someone fills out your Meta Instant Form or landing page.'}
        />
      ) : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <Table headers={['', 'Name', 'Email', 'Phone', 'Company', 'Source', 'Client', 'Status', 'Received', 'Actions']}>
            {/* Select-all header row */}
            <tr style={{ borderBottom: '1px solid #111c2d', background: '#0a1120' }}>
              <td style={{ padding: '8px 14px', width: 36 }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={toggleAll}
                  style={{ cursor: 'pointer' }}
                />
              </td>
              {(['Name', 'Email', null, null, 'Source', null, 'Status', 'Received', null] as const).map((col, i) => {
                const fieldMap: Record<string, SortField> = { Name: 'name', Email: 'email', Source: 'source', Status: 'status', Received: 'createdAt' };
                const field = col ? fieldMap[col] : undefined;
                return (
                  <td key={i}
                    onClick={() => field && toggleSort(field)}
                    style={{ ...thStyle(field), padding: '6px 14px', fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {col}{field ? sortIcon(field) : ''}
                  </td>
                );
              })}
            </tr>

            {filtered.map(l => (
              <tr key={l.id}
                style={{ borderBottom: '1px solid #111c2d', background: selected.has(l.id) ? '#0d1e35' : undefined }}
              >
                <td style={{ padding: '10px 14px', width: 36 }}>
                  <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleOne(l.id)} style={{ cursor: 'pointer' }} />
                </td>
                <td style={{ padding: '10px 14px', color: '#e8edf5', fontWeight: 500, maxWidth: 180 }}>
                  {l.name}
                  {l.notes && <div style={{ fontSize: 11, color: '#4a5d7a', marginTop: 2, fontStyle: 'italic' }}>{l.notes}</div>}
                </td>
                <td style={{ padding: '10px 14px', color: '#94a3b8', fontSize: 13 }}>
                  <a href={`mailto:${l.email}`} style={{ color: '#3b82f6', textDecoration: 'none' }}>{l.email}</a>
                </td>
                <td style={{ padding: '10px 14px', color: '#94a3b8', fontSize: 13 }}>
                  {l.phone ? <a href={`tel:${l.phone}`} style={{ color: '#3b82f6', textDecoration: 'none' }}>{l.phone}</a> : '-'}
                </td>
                <td style={{ padding: '10px 14px', color: '#94a3b8', fontSize: 13 }}>{l.company ?? '-'}</td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{sourceLabel(l.source)}</td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>
                  {l.clientId ? clientMap[l.clientId] ?? '-' : '-'}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <select
                    value={l.status ?? 'new'}
                    onChange={e => updateStatus(l, e.target.value as Lead['status'])}
                    style={{
                      background: '#0d1825', border: '1px solid #1a2535',
                      borderRadius: 6, color: STATUS_COLORS[l.status ?? 'new'] ?? '#e8edf5',
                      padding: '4px 8px', fontSize: 12, cursor: 'pointer',
                    }}
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="converted">Converted</option>
                    <option value="lost">Lost</option>
                  </select>
                </td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                  {l.createdAt ? new Date(l.createdAt).toLocaleDateString() : '-'}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <Btn size="sm" variant="ghost"
                    onClick={() => { setNoteModal(l); setNoteText(l.notes ?? ''); }}>
                    Note
                  </Btn>
                </td>
              </tr>
            ))}
          </Table>
          <div style={{ padding: '8px 16px', fontSize: 12, color: '#4a5d7a', borderTop: '1px solid #111c2d' }}>
            {filtered.length} lead{filtered.length !== 1 ? 's' : ''}{someSelected ? ` | ${selectedList.length} selected` : ''}
          </div>
        </Card>
      )}

      {/* Note modal */}
      <Modal open={!!noteModal} title={noteModal ? `Note: ${noteModal.name}` : ''} onClose={() => setNoteModal(null)}>
        <Textarea
          rows={4}
          placeholder="Add a note about this lead..."
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          style={{ width: '100%', marginBottom: 16 }}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={() => setNoteModal(null)}>Cancel</Btn>
          <Btn variant="primary" loading={saving} onClick={saveNote}>Save</Btn>
        </div>
      </Modal>

      {/* Add lead modal */}
      <Modal open={addModal} title="Add Lead" onClose={() => setAddModal(false)}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          {[
            { label: 'Name *',   key: 'name',    placeholder: 'Full name',         type: 'text' },
            { label: 'Email *',  key: 'email',   placeholder: 'email@example.com', type: 'email' },
            { label: 'Company',  key: 'company', placeholder: 'Company name',      type: 'text' },
            { label: 'Phone',    key: 'phone',   placeholder: '+1 555 000 0000',   type: 'tel' },
          ].map(f => (
            <div key={f.key}>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{f.label}</div>
              <input
                type={f.type}
                placeholder={f.placeholder}
                value={(addForm as any)[f.key]}
                onChange={e => setAddForm(p => ({ ...p, [f.key]: e.target.value }))}
                style={{ ...SELECT_STYLE, width: '100%', boxSizing: 'border-box' }}
              />
            </div>
          ))}
          <div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Source</div>
            <select value={addForm.source} onChange={e => setAddForm(p => ({ ...p, source: e.target.value }))} style={{ ...SELECT_STYLE, width: '100%' }}>
              <option value="manual">Manual</option>
              <option value="landing-page">Landing Page</option>
              <option value="meta-lead-ad">Meta Lead Ad</option>
              <option value="referral">Referral</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Client</div>
            <select value={addForm.clientId} onChange={e => setAddForm(p => ({ ...p, clientId: e.target.value }))} style={{ ...SELECT_STYLE, width: '100%' }}>
              <option value="">-- No client --</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.clientName}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Status</div>
            <select value={addForm.status ?? 'new'} onChange={e => setAddForm(p => ({ ...p, status: e.target.value as Lead['status'] }))} style={{ ...SELECT_STYLE, width: '100%' }}>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="converted">Converted</option>
              <option value="lost">Lost</option>
            </select>
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Notes</div>
          <Textarea
            rows={3}
            placeholder="Optional notes..."
            value={addForm.notes}
            onChange={e => setAddForm(p => ({ ...p, notes: e.target.value }))}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={() => setAddModal(false)}>Cancel</Btn>
          <Btn variant="primary" loading={addSaving} onClick={addLead}>Add Lead</Btn>
        </div>
      </Modal>

      {/* Delete confirm modal */}
      <Modal open={confirmDelete} title="Delete leads?" onClose={() => setConfirmDelete(false)}>
        <p style={{ color: '#94a3b8', marginBottom: 20 }}>
          Permanently delete <strong style={{ color: '#f87171' }}>{selectedList.length} lead(s)</strong>? This cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={() => setConfirmDelete(false)}>Cancel</Btn>
          <Btn variant="primary" loading={bulkWorking} onClick={bulkDelete}
            style={{ background: '#7f1d1d', borderColor: '#7f1d1d' }}>
            Delete {selectedList.length} lead(s)
          </Btn>
        </div>
      </Modal>
    </div>
  );
}
